import { useState, useMemo, useEffect } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/lib/utils";
import { useCartStore } from "@/store/cartStore";
import type { ShopifyProduct } from "@/lib/shopify";
import { getColorHexSync } from "@/lib/colorService";
import { getJewelryGradient } from "@/lib/colorGradient";
import { EngravingOptions, getEngravingCount, ENGRAVING_PRICE, validateEngraving } from "@/components/EngravingOptions";
import { IconSelection, hasIconTag, ICON_PRICE } from "@/components/IconSelection";
import type { EngravingData } from "@/components/EngravingOptions";
import type { IconData } from "@/components/IconSelection";
import { alertToast } from "@/contexts/AlertToastContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChevronDown } from "lucide-react";
import DOMPurify from "dompurify";
import { motion, AnimatePresence } from "motion/react";
import { useStaticTranslation } from "@/lib/staticTranslations";

interface VariantQuickPickerProps {
  product: ShopifyProduct;
  open: boolean;
  onClose: () => void;
  defaultOptions?: Record<string, string>;
}

export const VariantQuickPicker = ({ product, open, onClose, defaultOptions }: VariantQuickPickerProps) => {
  const addItem = useCartStore((s) => s.addItem);
  const isMobile = useIsMobile();
  const { t: vt, isHebrew } = useStaticTranslation("variantPicker");
  
  const options = product.options || [];
  const variants = product.variants?.edges || [];
  
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const opt of options) {
      if (opt.name !== "Title" && opt.values.length > 0) {
        initial[opt.name] = defaultOptions?.[opt.name] || opt.values[0];
      }
    }
    return initial;
  });

  const [variantsExpanded, setVariantsExpanded] = useState(true);
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  const engravingCount = getEngravingCount(product.tags);
  const requiresEngraving = engravingCount > 0;
  const showIcons = hasIconTag(product.tags);
  const [engravingData, setEngravingData] = useState<EngravingData | null>(null);
  const [engravingErrors, setEngravingErrors] = useState<{ font?: string; texts?: string[] }>({});
  const [iconData, setIconData] = useState<IconData | null>(null);
  const [noEngraving, setNoEngraving] = useState(false);

  const wordsOption = options.find(opt => 
    opt.name === 'מילים' || opt.name.toLowerCase() === 'words'
  );
  const hasWordsOption = !!wordsOption;

  const selectedVariant = useMemo(() => {
    return variants.find((v) => {
      return v.node.selectedOptions.every(
        (so) => selectedOptions[so.name] === so.value || so.name === "Title"
      );
    })?.node;
  }, [selectedOptions, variants]);

  const getTotalWordCount = (): number => {
    if (!engravingData?.texts) return 0;
    return engravingData.texts.reduce((total, text) => {
      const words = text.trim().split(/\s+/).filter(Boolean);
      return total + words.length;
    }, 0);
  };

  useEffect(() => {
    if (!hasWordsOption || !product) return;
    let targetValue: string;
    if (noEngraving) {
      targetValue = '0';
    } else {
      const wordCount = getTotalWordCount();
      targetValue = Math.min(wordCount, 3).toString();
    }
    const wordsOptionName = wordsOption?.name || 'מילים';
    if (selectedOptions[wordsOptionName] !== targetValue) {
      setSelectedOptions(prev => ({ ...prev, [wordsOptionName]: targetValue }));
    }
  }, [engravingData, noEngraving, hasWordsOption, product, wordsOption?.name]);

  const handleOptionChange = (optionName: string, value: string) => {
    setSelectedOptions((prev) => ({ ...prev, [optionName]: value }));
  };

  const handleAdd = () => {
    if (!selectedVariant) return;
    if (requiresEngraving && !noEngraving) {
      const errors = validateEngraving(engravingData, engravingCount);
      if (Object.keys(errors).length > 0) {
        setEngravingErrors(errors);
        alertToast.error(vt("engravingError", "יש למלא את פרטי החריטה"), vt("engravingErrorDesc", "בחר פונט והזן טקסט לחריטה"));
        return;
      }
      setEngravingErrors({});
    }
    addItem({
      product,
      variantId: selectedVariant.id,
      variantTitle: selectedVariant.title,
      price: selectedVariant.price,
      quantity: 1,
      selectedOptions: selectedVariant.selectedOptions,
      variantImage: selectedVariant.image || undefined,
      engraving: requiresEngraving && engravingData && !noEngraving
        ? { font: engravingData.font, texts: engravingData.texts, price: ENGRAVING_PRICE }
        : undefined,
      icons: showIcons && iconData && iconData.iconIds.length > 0 ? iconData.iconIds : undefined,
    });
    onClose();
  };

  const displayImage = useMemo(() => {
    if (selectedVariant?.image?.url) return selectedVariant.image.url;
    return product.images?.edges?.[0]?.node?.url;
  }, [selectedVariant, product]);

  const isColorOption = (name: string) => {
    const lower = name.toLowerCase();
    return lower.includes("צבע") || lower.includes("color") || lower.includes("colour") || lower.includes("גוון") || lower.includes("metal") || lower.includes("מתכת");
  };

  const isWordsOption = (name: string) => {
    const lower = name.toLowerCase();
    return lower === 'מילים' || lower === 'words';
  };

  const filteredOptions = options.filter(
    (o) => o.name !== "Title" && o.values.length > 1 && !isWordsOption(o.name)
  );

  const descriptionHtml = product.descriptionHtml || "";
  const hasDescription = !!(descriptionHtml || product.description);

  // ─── Render ───────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent 
        className={cn(
          "p-0 bg-white border-none shadow-2xl flex flex-col overflow-hidden gap-0",
          isMobile 
            ? "max-w-[100vw] max-h-[95vh] rounded-none" 
            : "sm:max-w-[820px] max-h-[88vh] rounded-none"
        )}
        dir={isHebrew ? "rtl" : "ltr"}
      >

        {/* ── Desktop: side-by-side / Mobile: stacked ── */}
        <div className={cn(
          "flex-1 overflow-hidden",
          isMobile ? "flex flex-col" : "grid grid-cols-[1fr_1fr]"
        )}>

          {/* ─── RIGHT side (RTL) = Image ─── */}
          <div className={cn(
            "bg-white flex items-center justify-center overflow-hidden",
            isMobile ? "h-[240px] shrink-0" : "min-h-[400px]"
          )}>
            {displayImage && (
              <img 
                key={displayImage} 
                src={displayImage} 
                alt={product.title} 
                className={cn(
                  "object-contain animate-fade-in",
                  isMobile ? "max-h-[220px] max-w-[90%]" : "max-h-[380px] max-w-[90%]"
                )}
              />
            )}
          </div>

          {/* ─── LEFT side (RTL) = Info + Options ─── */}
          <div className={cn(
            "flex flex-col",
            isMobile ? "flex-1 overflow-hidden" : "overflow-y-auto"
          )}>
            {/* Title + Price header */}
            <div className="px-6 pt-6 pb-4">
              <h2 className="text-base font-medium text-foreground leading-snug mb-2" style={{ fontWeight: 400 }}>
                {product.title}
              </h2>
              {selectedVariant && (
                <span className="text-lg font-semibold text-primary">
                  {formatPrice(selectedVariant.price.amount, selectedVariant.price.currencyCode)}
                </span>
              )}
              {!selectedVariant && (
                <span className="text-sm text-destructive">{vt("variantUnavailable", "וריאנט לא זמין")}</span>
              )}
            </div>

            {/* Scrollable content area */}
            <div className={cn(
              "flex-1 px-6 pb-4 space-y-5",
              isMobile ? "overflow-y-auto overscroll-contain" : ""
            )}>

              {/* Options */}
              {filteredOptions.map((option) => (
                <div key={option.name} className="space-y-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {option.name}
                  </span>

                  {isColorOption(option.name) ? (
                    <TooltipProvider delayDuration={200}>
                      <div className="flex flex-wrap gap-2">
                        {option.values.map((value) => {
                          const hex = getColorHexSync(value);
                          const isSelected = selectedOptions[option.name] === value;
                          const isWhite = hex.toLowerCase() === "#ffffff" || hex.toLowerCase() === "#fffff0" || hex.toLowerCase() === "#f5f5f0" || hex.toLowerCase() === "#e8e8e8" || hex.toLowerCase() === "#e5e4e2";

                          return (
                            <Tooltip key={value}>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleOptionChange(option.name, value)}
                                  className={cn(
                                    "w-8 h-8 rounded-full border-2 transition-all relative",
                                    isSelected
                                      ? "border-foreground ring-1 ring-foreground/20 scale-110"
                                      : "border-border/60 hover:border-foreground/40"
                                  )}
                                  style={{ background: getJewelryGradient(hex) }}
                                >
                                  {isWhite && (
                                    <span className="absolute inset-[2px] rounded-full border border-border/50" />
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs">
                                {value}
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </TooltipProvider>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {option.values.map((value) => (
                        <button
                          key={value}
                          onClick={() => handleOptionChange(option.name, value)}
                          className={cn(
                            "px-3 py-1.5 text-xs border transition-all",
                            selectedOptions[option.name] === value
                              ? "border-foreground bg-foreground text-background font-medium"
                              : "border-border text-muted-foreground hover:border-foreground/40"
                          )}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Engraving section */}
              {requiresEngraving && (
                <div className="space-y-3">
                  {hasWordsOption && (
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="no-engraving-quick"
                        checked={noEngraving}
                        onCheckedChange={(checked) => {
                          setNoEngraving(checked === true);
                          if (checked) {
                            setEngravingData(null);
                            setEngravingErrors({});
                          }
                        }}
                      />
                      <label
                        htmlFor="no-engraving-quick"
                        className="text-sm cursor-pointer select-none text-muted-foreground"
                        style={{ fontWeight: 300 }}
                      >
                        {vt("noEngraving", "ללא חריטה")}
                      </label>
                    </div>
                  )}
                  {!noEngraving && (
                    <EngravingOptions
                      value={engravingData}
                      onChange={setEngravingData}
                      price={ENGRAVING_PRICE}
                      currencyCode="ILS"
                      engravingCount={engravingCount}
                      errors={engravingErrors}
                    />
                  )}
                </div>
              )}

              {/* Icons */}
              {showIcons && !noEngraving && (
                <IconSelection value={iconData} onChange={setIconData} />
              )}

              {/* Description - collapsible */}
              {hasDescription && (
                <div className="border-t border-border/30 pt-4">
                  <button
                    onClick={() => setVariantsExpanded(!variantsExpanded)}
                    className="flex items-center justify-between w-full text-right group"
                  >
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{vt("productDescription", "תיאור המוצר")}</span>
                    <ChevronDown className={cn(
                      "h-3.5 w-3.5 text-muted-foreground transition-transform",
                      variantsExpanded && "rotate-180"
                    )} />
                  </button>
                  <AnimatePresence initial={false}>
                    {variantsExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="pt-3">
                          {descriptionHtml ? (
                            <div 
                              className="text-sm text-muted-foreground leading-relaxed max-w-none rtl:text-right [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pr-5 [&_ol]:list-decimal [&_ol]:pr-5 [&_li]:mb-1 [&_strong]:font-bold [&_strong]:text-foreground [&_b]:font-bold [&_b]:text-foreground [&_em]:italic [&_a]:text-primary [&_a]:underline [&_h1]:text-base [&_h1]:font-bold [&_h1]:text-foreground [&_h1]:mb-2 [&_h2]:text-sm [&_h2]:font-bold [&_h2]:text-foreground [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-foreground [&_h3]:mb-1 [&_br]:block [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border/30 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-border/30 [&_th]:px-2 [&_th]:py-1 [&_th]:font-semibold [&_img]:max-w-full [&_img]:h-auto [&_span]:inline [&_div]:mb-1"
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(descriptionHtml, { ADD_TAGS: ['iframe'], ADD_ATTR: ['target', 'allowfullscreen', 'frameborder', 'style', 'class'] }) }}
                            />
                          ) : (
                            <p className="text-xs text-muted-foreground leading-relaxed" style={{ fontWeight: 300 }}>
                              {product.description}
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Add to cart button - sticky at bottom */}
            <div className="shrink-0 px-6 py-4 border-t border-border/20 bg-white">
              <button
                onClick={handleAdd}
                disabled={!selectedVariant || !selectedVariant.availableForSale}
                onMouseEnter={() => setIsButtonHovered(true)}
                onMouseLeave={() => setIsButtonHovered(false)}
                className="relative w-full h-12 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  backgroundColor: '#7e6b44',
                  fontFamily: "'Almoni DL', system-ui, sans-serif",
                  fontSize: '15px'
                }}
              >
                <div className="relative w-full h-full overflow-hidden">
                  <div 
                    className="absolute inset-0 flex items-center justify-center text-white transition-transform duration-500"
                    style={{ transform: isButtonHovered ? 'translateY(-100%)' : 'translateY(0)' }}
                  >
                    {selectedVariant?.availableForSale === false ? vt("outOfStock", "אזל מהמלאי") : vt("addToCart", "הוסף לסל")}
                  </div>
                  <div 
                    className="absolute inset-0 flex items-center justify-center text-white transition-transform duration-500"
                    style={{ transform: isButtonHovered ? 'translateY(0)' : 'translateY(100%)' }}
                  >
                    <svg viewBox="0 0 16 16" className="w-5 h-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 2.5A.5.5 0 0 1 .5 2H2a.5.5 0 0 1 .485.379L2.89 4H14.5a.5.5 0 0 1 .485.621l-1.5 6A.5.5 0 0 1 13 11H4a.5.5 0 0 1-.485-.379L1.61 3H.5a.5.5 0 0 1-.5-.5zM3.14 5l1.25 5h8.22l1.25-5H3.14zM5 13a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0zm9-1a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0z" />
                    </svg>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
